import { cursorCell, type PuzzleModel } from '@arrow/game-engine';
import type { CSSProperties } from 'react';
import { memo, type RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useSettings } from '../app/settings.ts';
import type { CellFx, Snapshot } from '../game/useGame.ts';
import { bestChain, cellLabel, clueContentText, flowStitch, solveOrder } from '../game/view.ts';
import { BoardOverlay } from './BoardOverlay.tsx';
import { type Camera, CameraStage } from './camera/CameraStage.tsx';
import { type CellViewport, flowTagPlacement } from './camera/geometry.ts';
import { type ClueSetting, fitClue } from './clueFit.ts';
import { Wedge } from './icons.tsx';

interface BoardProps {
	model: PuzzleModel;
	snap: Snapshot;
	label: string;
	onTapCell: (cell: number) => void;
	onTapClue: (clueId: string) => void;
	/**
	 * A big board (10×10 and up) prints at reading size. On a phone it's read through a
	 * camera (bigger than the screen); where the whole board fits at that size (desktop,
	 * tablet) it renders whole, with no camera.
	 */
	big?: boolean | undefined;
	/** The camera, when the big board is bigger than the screen. */
	camera?: Camera | undefined;
	/**
	 * A finished board: the answer being read back (its clue tapped), lit like a selection
	 * but with no cursor. Null shows the board at rest.
	 */
	reviewId?: string | null | undefined;
}

/**
 * The puzzle: one printed block. An ARIA grid of rows and cells, with the ink rules,
 * wedge arrows and Flow stitch drawn in an SVG over it, and the "FLOW ×n" tag.
 */
export function Board({ model, snap, label, onTapCell, onTapClue, big = false, camera, reviewId = null }: BoardProps) {
	const { game, flow, phase } = snap;
	// A finished grid rests without a cursor; an answer being read back lights its path.
	const playing = phase === 'playing';
	const cursor = playing ? cursorCell(model, game) : null;
	const activeId = playing ? (game.cursor?.answerId ?? null) : phase === 'done' ? reviewId : null;
	const solved = useMemo(() => new Set(game.solvedAnswers), [game.solvedAnswers]);
	const lockedSet = useMemo(() => {
		const set = new Set<number>();
		for (const id of game.solvedAnswers) for (const c of model.answers[id]?.cells ?? []) set.add(c);
		return set;
	}, [game.solvedAnswers, model]);
	// The answer path, each cell numbered from the clue so it wakes in order.
	const pathIndex = useMemo(() => {
		const map = new Map<number, number>();
		(activeId ? model.answers[activeId]?.cells : [])?.forEach((c, k) => map.set(c, k + 1));
		return map;
	}, [activeId, model]);
	const flagged = useMemo(() => new Set(game.flaggedCells), [game.flaggedCells]);
	const finishing = phase === 'finishing' || phase === 'card';
	const order = useMemo(
		() => (finishing ? solveOrder(model, game, flow) : new Map<string, number>()),
		[finishing, model, game, flow],
	);
	const best = useMemo(() => new Set(finishing ? bestChain(flow) : []), [finishing, flow]);

	// Flow: the shared cell floods and the tag emerges at the far end of the stitch.
	const { links } = snap;
	const stitch = useMemo(
		() => (links && links.chain.length > 1 ? flowStitch(model, links.chain[links.chain.length - 2]!, links.chain[links.chain.length - 1]!) : null),
		[links, model],
	);
	const flood = playing && stitch ? { cell: stitch.cross, serial: links!.serial } : null;

	const rows: React.ReactElement[] = [];
	for (let r = 0; r < model.height; r++) {
		const cells: React.ReactElement[] = [];
		for (let c = 0; c < model.width; c++) {
			const i = r * model.width + c;
			cells.push(
				<Cell
					key={i}
					model={model}
					cell={i}
					letter={game.entries[i] ?? ''}
					label={cellLabel(model, game, i, solved, snap.typing && cursor === i)}
					locked={lockedSet.has(i)}
					path={pathIndex.get(i) ?? 0}
					isCursor={cursor === i}
					flagged={flagged.has(i)}
					flood={flood?.cell === i ? flood.serial : 0}
					activeAnswerId={activeId}
					fx={snap.fx[i]}
					onTapCell={onTapCell}
					onTapClue={onTapClue}
				/>,
			);
		}
		rows.push(
			<div role="row" className="grid-row" key={r}>
				{cells}
			</div>,
		);
	}

	const gridRef = useRef<HTMLDivElement>(null);
	const boardRef = useRef<HTMLDivElement>(null);
	// With or without the camera the grid is a different element (the tree around it
	// changes), so the fit pass re-runs when the board moves in or out of it.
	// Larger clues (Settings): a higher floor, and a short cue where a clue still won't fit.
	const { largeClues } = useSettings();
	useFitClues(gridRef, model, camera !== undefined, largeClues);

	// Zoomed out to the whole board, the clue blocks read as pure graphics (arrows only).
	const far = camera?.view.level === 'whole';
	const style = { '--cols': model.width, '--rows': model.height, '--n': model.answerOrder.length } as CSSProperties;
	const board = (
		<div
			ref={boardRef}
			className={big || camera ? 'board big' : 'board'}
			style={style}
			data-phase={phase}
			data-clues={largeClues ? 'large' : undefined}
		>
			<div
				ref={gridRef}
				className="grid"
				role="grid"
				aria-label={label}
				aria-readonly={phase !== 'playing'}
				aria-activedescendant={cursor !== null ? cellId(cursor) : undefined}
				aria-describedby="grid-help"
				tabIndex={0}
				data-game-surface
			>
				{rows}
			</div>
			<BoardOverlay
				model={model}
				activeAnswerId={activeId}
				links={playing ? links : null}
				nudge={playing ? snap.nudge : null}
				order={order}
				best={best}
				// Hollow "spent" wedges mark progress mid-solve; a finished board keeps them solid.
				spent={far && playing ? solved : null}
			/>
			{playing && links && stitch && (
				<FlowTag key={links.serial} model={model} cell={stitch.end} n={links.n} view={camera?.ctl.viewport()} />
			)}
		</div>
	);
	if (!camera) return board;
	return (
		<CameraStage
			camera={camera}
			model={model}
			boardRef={boardRef}
			activeId={activeId}
			cursor={cursor}
			solvedCount={game.solvedAnswers.length}
			entries={game.entries}
			phase={phase}
		>
			{board}
		</CameraStage>
	);
}

export const cellId = (cell: number) => `cell-${cell}`;

/**
 * "FLOW ×n", emerging from the board where the stitch ends, then gone (CSS). Kept
 * inside what the player can see (the camera's frame on a big board) and off clue text.
 */
function FlowTag({ model, cell, n, view }: { model: PuzzleModel; cell: number; n: number; view?: CellViewport | undefined }) {
	const r = Math.floor(cell / model.width);
	const c = cell % model.width;
	// Placed once, against the frame the tag appeared in: it never hops mid-animation.
	const [frame] = useState(view);
	const { tx, above } = flowTagPlacement(
		cell,
		{ cols: model.width, rows: model.height, isClue: (i) => model.kinds[i] === 'clue' },
		frame,
	);
	const style = {
		left: `${((c + 0.5) / model.width) * 100}%`,
		top: `${((r + 0.5) / model.height) * 100}%`,
		'--tx': `${tx}%`,
		'--ty': above ? '-165%' : '70%',
	} as CSSProperties;
	return (
		<div className="flowtag" style={style} aria-hidden="true">
			FLOW ×{n}
		</div>
	);
}

/**
 * Fits every clue to its cell (clueFit.ts): the width axis condenses before the size
 * drops, never below `--clue-min`; a clue that still won't fit shows a short cue
 * (`--clue-cue: 1`, board.css). Measured at the selected weight. Re-run when the board
 * resizes, the web fonts arrive, the grid remounts (`mount`) or the clue size changes.
 */
function useFitClues(ref: RefObject<HTMLDivElement | null>, model: PuzzleModel, mount: unknown, large: boolean) {
	useLayoutEffect(() => {
		const grid = ref.current;
		if (!grid) return;
		const fit = () => {
			const css = getComputedStyle(grid);
			const num = (name: string, fallback: number) => parseFloat(css.getPropertyValue(name)) || fallback;
			const cellSize = grid.clientWidth / model.width;
			if (!cellSize) return;
			const floor = num('--clue-min', 8.5);
			const opts = {
				floor,
				size0: Math.max(floor, Math.min(num('--clue-max', 10.5), cellSize * num('--clue-scale', 0.205))),
				wdth0: num('--clue-wdth', 84),
				wdthMin: num('--clue-wdth-min', 62),
				cue: css.getPropertyValue('--clue-cue').trim() === '1',
			};
			const weightOn = css.getPropertyValue('--clue-w-on').trim() || '700';
			for (const el of grid.querySelectorAll<HTMLElement>('.cl')) {
				const full = el.querySelector<HTMLElement>('.cl-t');
				const cue = el.querySelector<HTMLElement>('.cl-c');
				if (!full || !cue) continue;
				const apply = (s: ClueSetting) => {
					el.style.setProperty('--fs', `${s.size}px`);
					el.style.setProperty('--wd', String(s.wdth));
					if (s.tight) el.dataset.tight = '';
					else delete el.dataset.tight;
					if (s.cue) el.dataset.cue = '';
					else delete el.dataset.cue;
					cue.textContent = s.cue ?? '';
				};
				// The glyphs' boxes (a Range over the text, not its line boxes, which the type
				// outgrows at these leadings) against the cell's padding box. Centred text spills
				// both ways, and scrollHeight never sees the half above the top.
				const range = document.createRange();
				const over = () => {
					range.selectNodeContents(el.dataset.cue !== undefined ? cue : full);
					const t = range.getBoundingClientRect();
					if (!t.width && !t.height) return false;
					const r = el.getBoundingClientRect();
					const k = el.offsetHeight ? r.height / el.offsetHeight : 1;
					const top = r.top + el.clientTop * k;
					const left = r.left + el.clientLeft * k;
					const e = 0.5 * k;
					return t.top < top - e || t.left < left - e || t.bottom > top + el.clientHeight * k + e || t.right > left + el.clientWidth * k + e;
				};
				// Fit at the selected clue's weight, so selecting it never pushes it out.
				el.style.fontWeight = weightOn;
				fitClue(full.textContent ?? '', { apply, over }, opts);
				el.style.fontWeight = '';
			}
		};
		fit();
		let cancelled = false;
		document.fonts?.ready.then(() => !cancelled && fit());
		const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
		observer?.observe(grid);
		return () => {
			cancelled = true;
			observer?.disconnect();
		};
	}, [ref, model, mount, large]);
}

interface CellProps {
	model: PuzzleModel;
	cell: number;
	letter: string;
	label: string;
	locked: boolean;
	/** 1-based place in the selected answer (0 = not on the path). */
	path: number;
	isCursor: boolean;
	flagged: boolean;
	/** Non-zero while Flow floods this cell (the hand-off's serial). */
	flood: number;
	activeAnswerId: string | null;
	fx: CellFx | undefined;
	onTapCell: (cell: number) => void;
	onTapClue: (clueId: string) => void;
}

const Cell = memo(function Cell(props: CellProps) {
	const { model, cell, fx, label, letter } = props;
	const kind = model.kinds[cell];

	if (kind === 'clue') {
		const clueIds = model.cluesByCell[cell] ?? [];
		return (
			<div role="gridcell" id={cellId(cell)} aria-label={label} className="cell clue" data-count={clueIds.length}>
				{clueIds.map((id) => {
					const clue = model.clues[id]!;
					const on = clue.answerId === props.activeAnswerId;
					return (
						<div key={id} className={on ? 'cl on' : 'cl'} aria-hidden="true" onClick={() => props.onTapClue(id)}>
							<span className="cl-t">{clueContentText(clue.content)}</span>
							{/* The short cue (Larger clues), written by the fit pass. */}
							<span className="cl-c" />
						</div>
					);
				})}
			</div>
		);
	}

	if (kind !== 'letter') {
		return (
			<div role="gridcell" id={cellId(cell)} aria-label={label} className={cell === 0 ? 'cell blank corner' : 'cell blank'}>
				{cell === 0 && <Wedge direction="diagonal" className="corner-mark" />}
			</div>
		);
	}

	const className = [
		'cell',
		'letter',
		props.locked && 'solved',
		props.path && 'path',
		props.isCursor && 'cursor',
		props.flagged && 'flagged',
		props.flood && 'flooded',
	]
		.filter(Boolean)
		.join(' ');
	const style = {
		'--k': props.path,
		'--w': fx?.wave?.w ?? 0,
	} as CSSProperties;

	return (
		<div
			role="gridcell"
			id={cellId(cell)}
			aria-label={label}
			aria-selected={props.path > 0}
			className={className}
			style={style}
			onClick={() => props.onTapCell(cell)}
		>
			{fx?.sweep && (
				<span
					key={`s${fx.sweep.serial}`}
					className={fx.sweep.axis === 'down' ? 'fx fx-sweep down' : 'fx fx-sweep'}
					aria-hidden="true" style={{ '--i': fx.sweep.i } as CSSProperties} />
			)}
			{fx?.cross && (
				<span key={`x${fx.cross.serial}`} className="fx fx-cross" aria-hidden="true" style={{ '--i': fx.cross.i } as CSSProperties} />
			)}
			{props.flood ? <span key={`f${props.flood}`} className="fx fx-flood" aria-hidden="true" /> : null}
			<span key={`p${fx?.pop ?? 0}`} className={fx?.pop ? 'ch pop' : 'ch'} aria-hidden="true">
				{letter}
			</span>
		</div>
	);
});
